import {defaultTransformFn, dsApi} from "./DsApi";
import {Business} from "../entities/Business";
import {Plan} from "../entities/Plan";
import {PaymentMethodCard} from "../entities/PaymentMethodCard";
import {PaymentCard} from "../entities/PaymentCard";
import {SetupIntentResponse} from "../entities/PaymentIntent";
import moment from "moment";

export interface IAddPlanToBusinessRequest {
    planName: string;
    businessId: string;
    productId: string;
    addAddonRequests: IAddonRequest[];
    discount: number;
    isOverrideSetupFee: boolean;
    overrideSetupFee: number;
    startDate: moment.Moment;
}

export interface IAddonRequest {
    addonId: string;
    contractLength?: number;
    startDate?: moment.Moment;
    additionalAddons?: IAddonRequest[];
}

const businessesTags = dsApi.enhanceEndpoints({
    addTagTypes: [
        "Businesses",
        "Business",
        "BusinessPlans",
        "BusinessPaymentMethod",
    ],
});

export const businessesApi = businessesTags.injectEndpoints({
    endpoints: (build) => ({
        getBusinesses: build.query<Business[], void>({
            query: () => {
                return {
                    url: `businesses`,
                    method: "GET",
                };
            },
            transformResponse: defaultTransformFn,
            providesTags: () => [{type: "Businesses"}],
        }),
        getBusiness: build.query<Business, { businessId: string }>({
            query: ({businessId}) => {
                return {
                    url: `businesses/${businessId}`,
                    method: "GET",
                };
            },
            transformResponse: defaultTransformFn,
            providesTags: (result, a, request) => [
                {type: "Business", id: request.businessId},
            ],
        }),
        createBusiness: build.mutation<Business, { business: Business }>({
            query: ({business}) => {
                return {
                    url: `businesses`,
                    method: "POST",
                    data: {
                        name: business.name,
                        clientFirstName: business.clientFirstName,
                        clientLastName: business.clientLastName,
                        address: business.address,
                        phone: business.phone,
                        email: business.email,
                        website: business.website,
                        description: business.description,
                    },
                };
            },
            transformResponse: defaultTransformFn,
            invalidatesTags: [{type: "Businesses"}],
        }),
        updateBusiness: build.mutation<
            Business,
            { id: string; business: Business }
        >({
            query: ({id, business}) => {
                return {
                    url: `businesses/${id}`,
                    method: "PUT",
                    data: {
                        name: business.name,
                        clientFirstName: business.clientFirstName,
                        clientLastName: business.clientLastName,
                        address: business.address,
                        phone: business.phone,
                        email: business.email,
                        website: business.website,
                        description: business.description,
                        growthCoachNotes: business.growthCoachNotes,
                        adCritterNotes: business.adCritterNotes,
                        creativesUrl: business.creativesUrl,
                    },
                };
            },
            transformResponse: defaultTransformFn,
            invalidatesTags: (result, a, request) => [
                {type: "Businesses"},
                {type: "Business", id: request.id},
            ],
        }),
        getBusinessPlans: build.query<Plan[], { businessId: string }>({
            query: ({businessId}) => {
                return {
                    url: `businesses/${businessId}/plans`,
                    method: "GET",
                };
            },
            transformResponse: defaultTransformFn,
            providesTags: (result, a, request) => [
                {type: "BusinessPlans", id: request.businessId},
            ],
        }),
        reactivateBusinessPlan: build.mutation<
            Plan,
            { businessId: string; planId: string }
        >({
            query: ({planId}) => {
                return {
                    url: `plans/${planId}/reactivate`,
                    method: "POST",
                };
            },
            transformResponse: defaultTransformFn,
            invalidatesTags: (result, error, arg) => [
                {type: "BusinessPlans", id: arg.businessId},
            ],
        }),
        endBusinessPlan: build.mutation<
            Plan,
            { businessId: string; planId: string }
        >({
            query: ({planId}) => {
                return {
                    url: `plans/${planId}/end`,
                    method: "POST",
                };
            },
            transformResponse: defaultTransformFn,
            invalidatesTags: (result, error, arg) => [
                {type: "BusinessPlans", id: arg.businessId},
            ],
        }),
        addPlanToBusiness: build.mutation<
            { planId: string; businessId: string },
            IAddPlanToBusinessRequest
        >({
            query: ({
                        planName,
                        businessId,
                        productId,
                        addAddonRequests,
                        discount,
                        isOverrideSetupFee,
                        overrideSetupFee,
                        startDate,
                    }) => {
                return {
                    url: `businesses/${businessId}/plans`,
                    method: "POST",
                    data: {
                        planName: planName,
                        productId: productId,
                        addAddonRequests: addAddonRequests,
                        discount: discount,
                        isOverrideSetupFee: isOverrideSetupFee,
                        overrideSetupFee: overrideSetupFee,
                        startDate: startDate,
                    },
                };
            },
            transformResponse: defaultTransformFn,
            invalidatesTags: (result, a, request) => [
                {type: "BusinessPlans", id: request.businessId},
            ],
        }),
        addAddonsToPlan: build.mutation<
            Business,
            { businessId: string; planId: string; addAddonRequests: IAddonRequest[] }
        >({
            query: ({businessId, planId, addAddonRequests}) => {
                return {
                    url: `businesses/${businessId}/plans/${planId}/addAddons`,
                    method: "POST",
                    data: {
                        addAddonRequests: addAddonRequests,
                    },
                };
            },
            transformResponse: defaultTransformFn,
            invalidatesTags: (result, a, request) => [
                {type: "BusinessPlans", id: request.businessId},
            ],
        }),
        updatePaymentMethod: build.mutation<
            Business,
            { businessId: string; intuitToken: string }
        >({
            query: ({businessId, intuitToken}) => {
                return {
                    url: `businesses/${businessId}/updatePaymentMethod`,
                    method: "POST",
                    data: {
                        paymentMethodToken: intuitToken,
                    },
                };
            },
            transformResponse: defaultTransformFn,
            invalidatesTags: (result, a, request) => [
                {type: "Business", id: request.businessId},
                {type: "BusinessPaymentMethod", id: request.businessId},
            ],
        }),
        getDefaultPaymentMethod: build.query<
            PaymentMethodCard,
            { businessId: string }
        >({
            query: ({businessId}) => {
                return {
                    url: `businesses/${businessId}/paymentMethod`,
                    method: "GET",
                };
            },
            transformResponse: defaultTransformFn,
            providesTags: (result, a, request) => [
                {type: "BusinessPaymentMethod", id: request.businessId},
            ],
        }),
        addPaymentMethodCard: build.mutation<
            PaymentCard,
            { intuitToken: string; businessId: string }
        >({
            query: ({intuitToken, businessId}) => {
                return {
                    url: `payments/addPaymentMethodCard`,
                    method: "POST",
                    data: {
                        token: intuitToken,
                        businessId: businessId,
                    },
                };
            },
            transformResponse: defaultTransformFn,
        }),
        createSetupIntent: build.mutation<
            SetupIntentResponse,
            { businessId: string }
        >({
            query: ({businessId}) => {
                return {
                    url: `stripe/setupIntent`,
                    method: "POST",
                    data: {
                        businessId: businessId,
                    },
                };
            },
            transformResponse: defaultTransformFn,
        }),
        syncCards: build.mutation<void, { businessId: string }>({
            query: ({businessId}) => {
                return {
                    url: `stripe/${businessId}/syncCards`,
                    method: "POST",
                    data: {},
                };
            },
            transformResponse: defaultTransformFn,
            invalidatesTags: (result, a, request) => [
                {type: "BusinessPaymentMethod", id: request.businessId},
            ],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetBusinessesQuery,
    useGetBusinessQuery,
    useUpdateBusinessMutation,
    useCreateBusinessMutation,
    useGetBusinessPlansQuery,
    useAddPlanToBusinessMutation,
    useAddAddonsToPlanMutation,
    useUpdatePaymentMethodMutation,
    useGetDefaultPaymentMethodQuery,
    useAddPaymentMethodCardMutation,
    useCreateSetupIntentMutation,
    useSyncCardsMutation,
    useReactivateBusinessPlanMutation,
    useEndBusinessPlanMutation,
} = businessesApi;
